import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { LOANFORMAT_ALL_OBJECT } from '../../../store/loanFormat/actions';
import { MATERIAL_ICON_DOWNLOAD, MATERIAL_ICON_VIDEO } from '../../../shared/common/Button/Icon';
import { FilterBar } from '../../../shared/common/FilterBar';
import Pagination, { getPaginationStateObject } from '../../../shared/common/Pagination';
import ProductList from '../../../shared/common/ProductList';
import productList from '../../../shared/common/ProductList';
import ProductTile from '../../../shared/common/ProductTile';
import NoElementsFound, { NO_ELEMENTS_PRODUCTS } from '../../../shared/components/NoElementsFound';
import VideoPlayerPopup from '../../../shared/components/VideoPlayerPopup';
import ContentBaseLayout from '../../libx/shared/ContentBaseLayout';
import { DISPLAY_TYPE_LIST } from '../../../store/displayType/types';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import { AppState } from '../../../store/appState';
import { useHistory, useLocation, useParams } from 'react-router';
import { getLoanFormat } from '../../../shared/utils/getLoanFormat';
import queryString from 'query-string';
import { useQueryParams } from '../../../shared/hooks/useQueryParams';
import { ProductSorting } from '../../../../models/Product';
import { AgeCategory } from '../../../../models/AgeCategory';
import { deserializeFilter } from '../../../shared/utils/deserializeFilter';
import {
    invalidateYourCampaignProducts,
    useYourCampaignProducts
} from '../../../api/marketing/your-campaigns/useYourCampaignProducts';
import { modifyURLState, modifyURLStates } from '../../../shared/utils/utils-routing';
import { useUpdateCampaignSettings } from '../../../api/campaign-products/useUpdateCampaignSettings';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchYourCampaignsAggregationsQuickSearch } from '../../../api/marketing/your-campaigns/fetchYourCampaignsAggregationsQuickSearch';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { AuthenticationState } from '../../../store/authentication/state';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchYourCampaignsAggregations } from '../../../api/marketing/your-campaigns/fetchYourCampaignsAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';
import { CampaignListItem } from './CampaignListItem';
import { downloadMarketingMaterial } from '../../../api/marketing/your-campaigns/downloadMarketingMaterial';
import { downloadAnalyticsPdf } from '../../../api/marketing/your-campaigns/downloadAnalyticsPdf';
import { downloadCampaignGuide } from '../../../api/marketing/your-campaigns/downloadCampaignGuide';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useAddToList } from '../../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../../shared/common/CheckBeforeAddingModal';
import Utils from '../../../shared/utils';
import { useAgeCategorySearchParam } from '../../../shared/utils/useAgeCategorySearchParam';
import { UnavailableProductsModal } from '../../../shared/common/UnavailableProductsModal';

const SORT_BY_QUERY_PARAM = 'sortBy';
const PAGE_QUERY_PARAM = 'page';

export const DEFAULT_PAGE_SIZE = 54;

const StFilterBar = styled(FilterBar)`
    margin: 10px 0 40px 0;
`;

const StButtonLine = styled.div.attrs({ 'data-displayname': 'HeaderButtonLine' })`
    margin-bottom: 62px;

    * ~ * {
        margin-left: 8px;
    }
`;

export const YourCampaignsPage: FC = () => {
    const { t } = useTranslation();
    const authentication = useSelector((state: AppState) => state.authentication);
    const history = useHistory();
    const { search } = useLocation();
    const dispatch = useDispatch();
    const [showIntroVideo, setShowIntroVideo] = useState(false);
    const [urlParamHandled, setUrlParamHandled] = useState(false);
    const { loanFormat: loanFormatPathParam } = useParams<{ loanFormat: string }>();
    const loanFormat = getLoanFormat(loanFormatPathParam);

    /**
     * The `displayFilter` query param is either there or not. There is no value attached to it.
     * Examples:
     *      with:       `/collection/by-genre/all?displayFilter
     *      without:    `/collection/by-genre/all
     *
     * Parsing this query param with the `query-string` library will result in either null value or undefined.
     * This is the way how we can identify if the filter should be open or not.
     * Unfortunately, we can not replicate this behaviour with the `useQueryParams` hook.
     *
     * Of course it would be nicer to have a boolean value.
     * But that is a breaking change in regard to the URL structure.
     */
    const { displayFilter: displayFilterQueryParam } = queryString.parse(search);
    const displayFilter = displayFilterQueryParam === null;

    const {
        page: { value: pageParam = 1 },
        pageSize: { value: pageSizeParam },
        sortBy: { value: sortByParam },
        filter: { value: filterQueryParam }
    } = useQueryParams({
        [PAGE_QUERY_PARAM]: 'integer',
        [SORT_BY_QUERY_PARAM]: 'string',
        pageSize: 'integer',
        filter: 'string'
    });
    const page = pageParam - 1 ?? 0;
    const sortBy = sortByParam ?? ProductSorting.RELEASE_DATE_NEW_TO_OLD;
    const pageSize = pageSizeParam ?? DEFAULT_PAGE_SIZE;
    const { value: ageCategory } = useAgeCategorySearchParam();
    const filter = deserializeFilter(filterQueryParam);

    const { products, isLoading: isCampaignProductsLoading } = useYourCampaignProducts({
        filterMap: filter,
        pagination: { sortBy, page, pageSize },
        ageCategory,
        loanFormat: loanFormat.key === LOANFORMAT_ALL_OBJECT.key ? undefined : loanFormat.key!
    });

    const { mutate: updateCampaignSetting } = useUpdateCampaignSettings();

    const totalProducts = products?.count;
    const campaignProducts = products?.list;

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => yourCampaignsPageFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'byGenrePage',
            loanFormat.requestKey,
            'aggregation',
            ageCategory,
            filters,
            expandedFilterGroups
        ],
        [ageCategory, loanFormat.requestKey]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options =>
            fetchYourCampaignsAggregations({
                ...options,
                loanFormat: loanFormat.requestKey!,
                ageCategory
            }),
        [ageCategory, loanFormat.requestKey]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchYourCampaignsAggregationsQuickSearch({ ...options, loanFormat: loanFormat.requestKey!, ageCategory }),
        [ageCategory, loanFormat.requestKey]
    );
    //endregion

    useEffect(() => {
        if (search.includes('intro') && !urlParamHandled) {
            setShowIntroVideo(true);
            setUrlParamHandled(true);
        }
    }, [search, urlParamHandled]);

    const loading = isCampaignProductsLoading;

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({ callback: () => {} }); // note: we don't have to invalidate the products call, since we don't show a "items in cart" icon on this page

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    function handleDownloadMarketingMaterial(productId) {
        return downloadMarketingMaterial(productId);
    }

    function handleDownloadAnalyticsPdf(productId) {
        return downloadAnalyticsPdf(productId);
    }

    function handleDownloadCampaignGuide() {
        downloadCampaignGuide();
    }

    function updateProductCampaignSettings(startDate, endDate, campaignTagLine, productId) {
        updateCampaignSetting(
            {
                campaignProductId: productId,
                settings: {
                    startDate,
                    endDate,
                    campaignTagLine
                }
            },
            {
                onSuccess: () => {
                    invalidateYourCampaignProducts().then();
                }
            }
        );
    }

    function onFilterDisplayToggle() {
        const nextSearch = modifyURLState(history.location.search, 'displayFilter', !displayFilter, false);
        history.replace({ pathname: history.location.pathname, search: nextSearch });
    }

    const setSortBy = nextSortBy => {
        const pageQueryState = getPaginationStateObject();
        const sortByQueryState = { name: SORT_BY_QUERY_PARAM, value: nextSortBy, encode: false };

        const nextSearch = modifyURLStates(history.location.search, [pageQueryState, sortByQueryState]);

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    const setPage = nextPage => {
        const nextSearch = modifyURLState(
            history.location.search,
            PAGE_QUERY_PARAM,
            nextPage !== 0 ? nextPage + 1 : undefined,
            false
        );

        history.replace({ pathname: history.location.pathname, search: nextSearch });
    };

    return (
        <>
            <ContentBaseLayout
                topComponent={
                    <>
                        <StButtonLine>
                            <Button
                                onClick={() => setShowIntroVideo(true)}
                                text={t('marketing.yourCampaigns.showIntroVideo')}
                                size={ButtonSize.LARGE}
                                theme={ButtonTheme.NEUTRAL}
                                icon={MATERIAL_ICON_VIDEO}
                            />
                            <Button
                                onClick={handleDownloadCampaignGuide}
                                text={t('marketing.yourCampaigns.downloadCampaignGuide')}
                                icon={MATERIAL_ICON_DOWNLOAD}
                                size={ButtonSize.LARGE}
                                theme={ButtonTheme.NEUTRAL}
                            />
                        </StButtonLine>
                        <StFilterBar
                            hideSelectButtons={true}
                            hideGroupBy={true}
                            sortBy={sortBy}
                            hideDisplayType={true}
                            totalProducts={totalProducts}
                            onSortChange={setSortBy}
                            dispatch={dispatch}
                            currentPage={page}
                            pageSize={pageSize}
                            onPageChange={setPage}
                            onFilterDisplayToggle={onFilterDisplayToggle}
                            displayFilter={displayFilter}
                        />
                    </>
                }
                sidebarComponent={
                    displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                        />
                    ) : null
                }
                contentComponent={
                    <div className={classNames({ 'min-h-screen': isCampaignProductsLoading }, 'ca-main')}>
                        {productList.length === 0 && !loading ? (
                            <NoElementsFound type={NO_ELEMENTS_PRODUCTS} />
                        ) : (
                            <div className="ca-main-inner">
                                <ProductList
                                    products={campaignProducts}
                                    displayType={DISPLAY_TYPE_LIST}
                                    linkToAllLanguagePage={false}
                                    forceStoreLoanFormat={true}
                                    listItemComponent={CampaignListItem}
                                    downloadMarketingMaterial={handleDownloadMarketingMaterial}
                                    onDownloadAnalyticsPdf={handleDownloadAnalyticsPdf}
                                    updateProductCampaignSettings={updateProductCampaignSettings}
                                    tileItemComponent={ProductTile}
                                    onAddPrice={changePrice}
                                    isProductSelectable={false}
                                    isInPartnerContext={Utils.isInPartnerContext(authentication)}
                                    currentPage={page}
                                />
                                <Pagination
                                    totalItems={totalProducts}
                                    onPageChange={setPage}
                                    currentPage={page}
                                    pageSize={pageSize}
                                />
                            </div>
                        )}
                    </div>
                }
                modalComponent={
                    <VideoPlayerPopup
                        showVideo={showIntroVideo}
                        hideVideo={() => setShowIntroVideo(false)}
                        vimeoId={354627777}
                    />
                }
                documentTitle={t('marketing.yourCampaigns.yourCampaigns')}
                loading={loading}
            />
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </>
    );
};

const yourCampaignsPageFilterGroups = (authentication: AuthenticationState, ageCategory: AgeCategory) => [
    ...defaultVisibleFilterGroups(authentication, ageCategory),
    'campaignStatus'
];
